/**
 * AdminChangeRequestProcessPage — [UC-024]
 * Admin processes a user-submitted change request atomically:
 *  - optionally edits description / target screen,
 *  - uploads a new preview link,
 *  - backend transitions CR to Finished / AdminProcessed in one call.
 * Backend returns the full ProjectResponse so we navigate straight back
 * to the project detail and it reloads cleanly.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components/ui';
import { Select } from '../components/ui/Select';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface ChangeRequest {
  changeRequestId: number;
  projectId: number;
  screenId: number | null;
  title: string;
  description: string;
  priority: string;
  status: string;
  creationDate: string;
  resolutionMethod?: string;
  newPreviewLink?: string;
  originalPreviewLink?: string;
}

interface Screen {
  screenId: number;
  name: string;
  projectId: number;
  filePath?: string;
}

const URL_RE =
  /^https?:\/\/(?:(?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\.)+[A-Z]{2,6}\.?|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d+)?(?:\/?|[/?]\S+)$/i;

export function AdminChangeRequestProcessPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [changeRequest, setChangeRequest] = useState<ChangeRequest | null>(null);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [urlError, setUrlError] = useState('');

  // Form state (seeded from the loaded CR).
  const [newPreviewLink, setNewPreviewLink] = useState('');
  const [selectedScreenId, setSelectedScreenId] = useState<number | null>(null);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        // Fetch the CR first so we know projectId before loading screens.
        const cr = await api.get<ChangeRequest>(`/change-requests/${id}`);
        setChangeRequest(cr);
        setDescription(cr.description);
        setSelectedScreenId(cr.screenId);
        if (cr.projectId) {
          const screensResp = await api.get<{ items: Screen[] }>(
            `/projects/${cr.projectId}/screens`,
          );
          setScreens(screensResp.items || []);
        }
      } catch (error) {
        console.error('Failed to load change request:', error);
        toast.error(
          t('adminChangeRequestProcess.loadError', 'Failed to load change request'),
        );
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async () => {
    if (!changeRequest) return;

    const link = newPreviewLink.trim();
    if (!link) {
      setUrlError(
        t('adminChangeRequestProcess.urlRequired', 'New preview link is required'),
      );
      return;
    }
    if (!URL_RE.test(link)) {
      setUrlError(
        t('adminChangeRequestProcess.urlInvalid', 'Enter a valid URL (https://...)'),
      );
      return;
    }
    setUrlError('');

    try {
      setSubmitting(true);
      // One atomic call: CR finish + project preview update + email + audit.
      const payload: {
        newPreviewLink: string;
        description?: string;
        screenId?: number;
      } = { newPreviewLink: link };

      // Only send the optional fields when the admin actually changed them.
      if (description.trim() !== changeRequest.description.trim()) {
        payload.description = description.trim();
      }
      if (selectedScreenId && selectedScreenId !== changeRequest.screenId) {
        payload.screenId = selectedScreenId;
      }

      await api.patch(`/change-requests/${changeRequest.changeRequestId}/process`, payload);
      toast.success(
        t(
          'adminChangeRequestProcess.success',
          'Change request processed. User has been notified.',
        ),
      );
      navigate(`/admin/projects/${changeRequest.projectId}`);
    } catch (error) {
      const err = error as { response?: { status?: number; data?: { detail?: string } } };
      const detail = err?.response?.data?.detail;
      toast.error(
        detail ||
          t(
            'adminChangeRequestProcess.submitError',
            'Failed to process change request',
          ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!changeRequest) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <div className="text-center p-6">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              {t('adminChangeRequestProcess.notFoundTitle', 'Change Request Not Found')}
            </h1>
            <p className="text-gray-600 mb-4">
              {t(
                'adminChangeRequestProcess.notFoundBody',
                'The requested change request could not be found.',
              )}
            </p>
            <Button onClick={() => navigate('/admin/project-list')}>
              {t('adminChangeRequestProcess.backToProjects', 'Back to Projects')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const alreadyProcessed = changeRequest.status !== 'New';

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <Card>
        <div className="p-6">
          <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-6">
            {t('adminChangeRequestProcess.title', 'Process Change Request')}
          </h1>

          {/* Read-only details */}
          <div className="mb-6 p-4 bg-[var(--surface-raised)] rounded-lg border border-[var(--border-subtle)]">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">
              {t('adminChangeRequestProcess.requestDetails', 'Request Details')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-[var(--text-secondary)]">
                  {t('adminChangeRequestProcess.titleLabel', 'Title')}:
                </span>
                <span className="ml-2 text-[var(--text-primary)]">{changeRequest.title}</span>
              </div>
              <div>
                <span className="font-medium text-[var(--text-secondary)]">
                  {t('adminChangeRequestProcess.priority', 'Priority')}:
                </span>
                <span
                  className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                    changeRequest.priority === 'High'
                      ? 'bg-red-100 text-red-700'
                      : changeRequest.priority === 'Medium'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {changeRequest.priority}
                </span>
              </div>
              <div>
                <span className="font-medium text-[var(--text-secondary)]">
                  {t('adminChangeRequestProcess.status', 'Status')}:
                </span>
                <span className="ml-2 text-[var(--text-primary)]">{changeRequest.status}</span>
              </div>
              <div>
                <span className="font-medium text-[var(--text-secondary)]">
                  {t('adminChangeRequestProcess.created', 'Created')}:
                </span>
                <span className="ml-2 text-[var(--text-primary)]">
                  {new Date(changeRequest.creationDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {alreadyProcessed && (
            <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/25 rounded-lg text-sm text-amber-700 dark:text-amber-400">
              {t(
                'adminChangeRequestProcess.alreadyProcessed',
                'This change request is already processed. No further action can be taken.',
              )}
            </div>
          )}

          {/* Editable fields — description + screen + new preview link, all one submit */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {t('adminChangeRequestProcess.descriptionLabel', 'Description')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={alreadyProcessed || submitting}
                className="w-full p-3 bg-[var(--surface-base)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent"
                rows={4}
                placeholder={t(
                  'adminChangeRequestProcess.descriptionPlaceholder',
                  'Refine the description if needed…',
                )}
              />
            </div>

            <div>
              <Select<number>
                label={t('adminChangeRequestProcess.targetScreen', 'Target Screen')}
                placeholder={t('adminChangeRequestProcess.selectScreen', 'Select a screen')}
                value={selectedScreenId ?? undefined}
                onChange={(v) => setSelectedScreenId(v)}
                disabled={alreadyProcessed || submitting}
                options={screens.map((s) => ({ value: s.screenId, label: s.name }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {t('adminChangeRequestProcess.newPreviewLink', 'New Preview Link')} *
              </label>
              <Input
                type="url"
                value={newPreviewLink}
                onChange={(e) => {
                  setNewPreviewLink(e.target.value);
                  if (urlError) setUrlError('');
                }}
                placeholder="https://..."
                className="w-full"
                disabled={alreadyProcessed || submitting}
              />
              {urlError && (
                <p className="mt-1 text-xs text-red-600">{urlError}</p>
              )}
              <p className="mt-1 text-xs text-[var(--text-secondary)]">
                {t(
                  'adminChangeRequestProcess.newPreviewHelp',
                  "Upload the updated preview and paste its URL. The user will be emailed with the new link.",
                )}
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/projects/${changeRequest.projectId}`)}
                disabled={submitting}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={alreadyProcessed || submitting || !newPreviewLink.trim()}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {submitting
                  ? t('adminChangeRequestProcess.processing', 'Processing…')
                  : t(
                      'adminChangeRequestProcess.submit',
                      'Complete & notify user',
                    )}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AdminChangeRequestProcessPage;
